// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: reading pads without an editor. The All pads view summarises every pad
// (preview lines, open/done counts), gathers the open to-dos that have a due date,
// searches lines across pads, and ticks a line from outside the editor. Everything
// here works on the stored markdown, so it is pure and cheap to test.

import {DUE_PATTERN, fromDueMarkdown, parseDuePhrase} from '@app/features/konvo_scratchpad/components/ScratchpadDates';

export type PadLineKind = 'todo' | 'done' | 'bullet' | 'heading' | 'quote' | 'text';

export interface PadLine {
	/** Index of the line in the pad's markdown. */
	index: number;
	kind: PadLineKind;
	/** Readable text: chips become labels, the due marker is dropped. */
	text: string;
	/** The words as the editor reads them (no @ or # on chips), for finding the line again. */
	match: string;
	due: Date | null;
	/** Heading level for headings, 0 otherwise. */
	level: number;
}

/** A pad as the overview sees it: the personal pads and the shared chat pads alike. */
export interface PadSourceInfo {
	/** 'personal:<name>' or 'shared:<channelId>'. */
	key: string;
	label: string;
	content: string;
}

const TODO = /^\s*[-*+]\s+\[( |x|X)\]\s?(.*)$/;
const BULLET = /^\s*(?:[-*+]|\d{1,3}[.)])\s+(.*)$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const FENCE = /^```/;
const TOGGLE_START = /^:::toggle(?:\[open\])?(?: (.*))?$/;
const TOGGLE_END = /^:::$/;

/** Markdown of one line as plain words. `forMatch` drops the @ and # sigils so it lines up with the editor's text. */
export function plainLine(markdown: string, forMatch = false): string {
	return markdown
		.replace(new RegExp(DUE_PATTERN.source, 'g'), '')
		.replace(/\[([@#])([^\]\n]+)\]\(konvo:\/\/(?:user|channel)\/[A-Za-z0-9_-]+\)/g, (_all, sigil, name) =>
			forMatch ? name : `${sigil}${name}`,
		)
		.replace(/!?\[([^\]\n]*)\]\([^)\s]*\)/g, '$1')
		.replace(/(\*\*|__|~~|`)(.+?)\1/g, '$2')
		.replace(/(^|[\s(])\*(\S(?:.*?\S)?)\*(?=$|[\s).,!?])/g, '$1$2')
		.replace(/\\([\\`*_{}[\]()#+\-.!>~|])/g, '$1')
		.replace(/\s+/g, ' ')
		.trim();
}

function dueOf(markdown: string): Date | null {
	const match = DUE_PATTERN.exec(markdown);
	return match ? fromDueMarkdown(match[1], match[2]) : null;
}

function makeLine(index: number, kind: PadLineKind, markdown: string, due: Date | null, level = 0): PadLine {
	return {index, kind, text: plainLine(markdown), match: plainLine(markdown, true), due, level};
}

/** Every meaningful line of a pad, skipping code, blank lines and the archived "Done" toggle. */
export function parseLines(content: string): Array<PadLine> {
	const out: Array<PadLine> = [];
	const lines = content.split('\n');
	let inFence = false;
	// A stack of open toggles; true when the toggle is the archive of finished items.
	const toggles: Array<boolean> = [];
	for (let index = 0; index < lines.length; index++) {
		const raw = lines[index];
		if (FENCE.test(raw.trim())) {
			inFence = !inFence;
			continue;
		}
		if (inFence) continue;
		const trimmed = raw.trim();
		const toggle = TOGGLE_START.exec(trimmed);
		if (toggle) {
			const title = (toggle[1] ?? '').trim();
			toggles.push(title.toLowerCase() === 'done');
			if (title && !toggles.includes(true)) out.push(makeLine(index, 'text', title, null));
			continue;
		}
		if (TOGGLE_END.test(trimmed)) {
			toggles.pop();
			continue;
		}
		if (toggles.includes(true) || trimmed.length === 0 || /^(-{3,}|\*{3,})$/.test(trimmed)) continue;
		const todo = TODO.exec(raw);
		if (todo) {
			out.push(makeLine(index, todo[1] === ' ' ? 'todo' : 'done', todo[2], dueOf(todo[2])));
			continue;
		}
		const heading = HEADING.exec(trimmed);
		if (heading) {
			out.push(makeLine(index, 'heading', heading[2], null, heading[1].length));
			continue;
		}
		const quote = QUOTE.exec(trimmed);
		if (quote) {
			if (quote[1].trim()) out.push(makeLine(index, 'quote', quote[1], null));
			continue;
		}
		const bullet = BULLET.exec(raw);
		if (bullet) {
			out.push(makeLine(index, 'bullet', bullet[1], dueOf(bullet[1])));
			continue;
		}
		out.push(makeLine(index, 'text', trimmed, dueOf(trimmed)));
	}
	return out.filter((entry) => entry.text.length > 0 || entry.kind === 'todo' || entry.kind === 'done');
}

export interface PadSummary {
	/** The first line when it is a top-level heading: the pad's page title. */
	title: string | null;
	open: number;
	done: number;
	/** Number of lines with something on them. */
	lines: number;
	/** The first few lines after the title. */
	preview: Array<PadLine>;
}

export function summarisePad(content: string, previewSize = 4): PadSummary {
	const lines = parseLines(content);
	const first = lines[0];
	const title = first && first.kind === 'heading' && first.level === 1 ? first.text : null;
	const body = title ? lines.slice(1) : lines;
	return {
		title,
		open: lines.filter((line) => line.kind === 'todo').length,
		done: lines.filter((line) => line.kind === 'done').length,
		lines: lines.length,
		preview: body.filter((line) => line.kind !== 'heading' || line.level > 1).slice(0, previewSize),
	};
}

export interface DueItem {
	sourceKey: string;
	sourceLabel: string;
	line: PadLine;
	due: Date;
}

/** Open to-dos with a due date across all pads, soonest first. */
export function collectDueItems(sources: ReadonlyArray<PadSourceInfo>, limit = 6): Array<DueItem> {
	const items: Array<DueItem> = [];
	for (const source of sources) {
		for (const line of parseLines(source.content)) {
			if (line.kind !== 'todo' || !line.due) continue;
			items.push({sourceKey: source.key, sourceLabel: source.label, line, due: line.due});
		}
	}
	return items.sort((a, b) => a.due.getTime() - b.due.getTime()).slice(0, limit);
}

export interface LineHit {
	sourceKey: string;
	sourceLabel: string;
	line: PadLine;
}

/** Lines containing every word of the query, in pad order. Pad names match too. */
export function searchPadLines(sources: ReadonlyArray<PadSourceInfo>, query: string, limit = 30): Array<LineHit> {
	const words = query.toLowerCase().split(/\s+/).filter(Boolean);
	if (words.length === 0) return [];
	const hits: Array<LineHit> = [];
	for (const source of sources) {
		for (const line of parseLines(source.content)) {
			const haystack = line.text.toLowerCase();
			if (words.every((word) => haystack.includes(word))) {
				hits.push({sourceKey: source.key, sourceLabel: source.label, line});
				if (hits.length >= limit) return hits;
			}
		}
	}
	return hits;
}

/** Ticks or unticks the to-do on line `index`; anything else is returned unchanged. */
export function toggleTodoLine(content: string, index: number): string {
	const lines = content.split('\n');
	const line = lines[index];
	if (line === undefined) return content;
	const match = /^(\s*[-*+]\s+\[)( |x|X)(\].*)$/.exec(line);
	if (!match) return content;
	lines[index] = `${match[1]}${match[2] === ' ' ? 'x' : ' '}${match[3]}`;
	return lines.join('\n');
}

// ---------------------------------------------------------------------------
// The date hint: a date phrase at the end of the line being typed.

/** Single words that are dates on their own; short forms like "sun" or "sat" are ordinary words too often. */
const SINGLE_WORD_DATES = /^(tomorrow|tonight|monday|tuesday|wednesday|thursday|friday|saturday|sunday|weekend)$/;
/** Words a date phrase may lead with; they go when the phrase becomes a chip. */
const LEAD_WORDS = /^(by|on|due|at)$/;

export interface DatePhrase {
	/** The words at the end of the line that make the date, as typed. */
	phrase: string;
	date: Date;
}

function bareHour(candidate: string, now: Date): Date | null {
	const match = /^(.*\S)\s+(\d{1,2})$/.exec(candidate);
	if (!match) return null;
	let hour = Number(match[2]);
	if (hour > 23) return null;
	// "fri 3" means the afternoon; nobody plans a 03:00.
	if (hour >= 1 && hour <= 6) hour += 12;
	return parseDuePhrase(`${match[1]} at ${hour}`, now);
}

/**
 * The date phrase the line ends with ("call Robert tomorrow 9", "send it by fri at 3pm"),
 * or null. Past times and lone words that are usually not dates are ignored.
 */
export function findDatePhrase(text: string, now = new Date()): DatePhrase | null {
	const words = text.trim().split(/\s+/).filter(Boolean);
	for (let count = Math.min(5, words.length); count >= 1; count--) {
		const tail = words.slice(words.length - count);
		if (LEAD_WORDS.test(tail[0].toLowerCase()) && count > 1) continue;
		const candidate = tail.join(' ');
		const lower = candidate.toLowerCase();
		if (count === 1 && !SINGLE_WORD_DATES.test(lower)) continue;
		if (/^\d+$/.test(lower)) continue;
		const date = parseDuePhrase(lower, now) ?? bareHour(lower, now);
		if (!date || date.getTime() < now.getTime()) continue;
		const lead = words[words.length - count - 1];
		const phrase = lead && LEAD_WORDS.test(lead.toLowerCase()) ? `${lead} ${candidate}` : candidate;
		return {phrase, date};
	}
	return null;
}

/** "8:44", "yesterday", "Mon", "12 Sep". */
export function formatEdited(date: Date, now = new Date()): string {
	const start = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
	const days = Math.round((start(now) - start(date)) / 86_400_000);
	if (days <= 0) return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
	if (days === 1) return 'yesterday';
	if (days < 7) return date.toLocaleDateString(undefined, {weekday: 'short'});
	return date.toLocaleDateString(undefined, {day: 'numeric', month: 'short'});
}
